import { useState, useEffect, useCallback } from 'react';
import useAuthStore from '../stores/authStore';

/**
 * Hook to manage custom task ordering for All Tasks/My Tasks views
 * Stores order per user per view in localStorage
 */
export const useCustomTaskOrder = (viewMode, tasks) => {
  const { user } = useAuthStore();
  const storageKey = `taskOrder_${user?._id}_${viewMode}`;
  
  // Initialize order from localStorage or from current tasks
  const [taskOrder, setTaskOrder] = useState(() => {
    if (!user?._id || !viewMode) return [];
    
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading task order:', error);
    }
    
    // If no saved order, use current task IDs
    return tasks.map(t => t._id);
  });

  // Update localStorage when order changes
  useEffect(() => {
    if (!user?._id || !viewMode || taskOrder.length === 0) return;
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(taskOrder));
    } catch (error) {
      console.error('Error saving task order:', error);
    }
  }, [taskOrder, storageKey, user?._id, viewMode]);

  // Apply custom order to tasks
  const getOrderedTasks = useCallback((taskList) => {
    if (!taskOrder.length || !taskList.length) return taskList;

    const taskMap = new Map(taskList.map(task => [task._id, task]));
    const orderedTasks = [];
    
    // Add tasks in saved order
    taskOrder.forEach(taskId => {
      const task = taskMap.get(taskId);
      if (task) {
        orderedTasks.push(task);
        taskMap.delete(taskId);
      }
    });
    
    // Add any new tasks at the beginning
    const newTasks = Array.from(taskMap.values());
    
    return [...newTasks, ...orderedTasks];
  }, [taskOrder]);

  // Update order after drag and drop
  const updateTaskOrder = useCallback((newOrder) => {
    setTaskOrder(newOrder);
  }, []);

  // Reset to default order
  const resetOrder = useCallback(() => {
    setTaskOrder(tasks.map(t => t._id));
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error resetting task order:', error);
    }
  }, [tasks, storageKey]);

  return {
    getOrderedTasks,
    updateTaskOrder,
    resetOrder,
    hasCustomOrder: taskOrder.length > 0
  };
};

export default useCustomTaskOrder;